import { useMemo, useState } from "react"

import {
  useActiveSyncJobs,
  useTriggerSync,
  useTriggerKeywordsSync,
  useCancelSyncJob,
  useLastSyncInfo,
  useTikTokAccountsList,
} from "@/api/hooks/admin"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"

function showToast(msg: string, type: "success" | "error" = "success") {
  const el = document.createElement("div")
  el.textContent = msg
  el.className = `fixed top-4 right-4 z-[9999] rounded-lg px-4 py-3 text-sm font-medium shadow-lg ${
    type === "error"
      ? "bg-red-900/90 text-red-200 border border-red-700"
      : "bg-green-900/90 text-green-200 border border-green-700"
  }`
  document.body.appendChild(el)
  setTimeout(() => el.remove(), 3000)
}

// ---------------------------------------------------------------------------
// Date presets
// ---------------------------------------------------------------------------

function todayStr() {
  return new Date().toISOString().slice(0, 10)
}
function yesterdayStr() {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return d.toISOString().slice(0, 10)
}
function daysAgoStr(n: number) {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d.toISOString().slice(0, 10)
}

// ---------------------------------------------------------------------------
// Sync Modal
// ---------------------------------------------------------------------------

export function SyncModal({
  open,
  onClose,
  onStarted,
}: {
  open: boolean
  onClose: () => void
  onStarted?: () => void
}) {
  const [startDate, setStartDate] = useState(todayStr())
  const [endDate, setEndDate] = useState(todayStr())
  const [sources, setSources] = useState({
    tiktok: true,
    roots: true,
    adw: true,
    klow: true,
    adwKeywords: false,
  })
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set())
  const [selectAll, setSelectAll] = useState(true)

  const { data: accountsData } = useTikTokAccountsList()
  const triggerSync = useTriggerSync()
  const triggerKw = useTriggerKeywordsSync()

  const accounts = accountsData?.accounts ?? []

  function applyPreset(start: string, end: string) {
    setStartDate(start)
    setEndDate(end)
  }

  function toggleSelectAll() {
    if (selectAll) {
      setSelectedAccounts(new Set())
      setSelectAll(false)
    } else {
      setSelectedAccounts(new Set(accounts.map((a) => a.advertiser_id)))
      setSelectAll(true)
    }
  }

  function toggleAccount(advId: string) {
    setSelectedAccounts((prev) => {
      const next = new Set(prev)
      if (next.has(advId)) next.delete(advId)
      else next.add(advId)
      return next
    })
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const dataSources: string[] = []
    if (sources.tiktok) dataSources.push("tiktok")
    if (sources.roots) dataSources.push("roots")
    if (sources.adw) dataSources.push("adw")
    if (sources.klow) dataSources.push("klow")

    try {
      let lastMessage: string | undefined
      // Trigger main sync if any main source selected
      if (dataSources.length > 0) {
        const accountIds =
          selectAll || selectedAccounts.size === 0
            ? undefined
            : Array.from(selectedAccounts)
        const result = await triggerSync.mutateAsync({
          startDate,
          endDate,
          dataSources,
          accountIds,
        })
        lastMessage = result?.message
      }

      // Trigger keywords sync separately
      if (sources.adwKeywords) {
        const kwResult = await triggerKw.mutateAsync({ startDate, endDate })
        lastMessage = kwResult?.message ?? lastMessage
      }

      showToast(lastMessage || "Sync started")
      onStarted?.()
      onClose()
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : "Failed to start sync"
      showToast(message, "error")
    }
  }

  const isPending = triggerSync.isPending || triggerKw.isPending

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto border-[#4a4a4a] bg-[#2d2d2d] text-white sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Sync Data</DialogTitle>
          <DialogDescription className="text-white">
            Manually sync campaign data from TikTok and revenue from Roots, ADW, or KLOW.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full rounded border border-[#555] bg-[#3a3a3a] px-3 py-2 text-sm text-white"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full rounded border border-[#555] bg-[#3a3a3a] px-3 py-2 text-sm text-white"
                required
              />
            </div>
          </div>

          {/* Date presets */}
          <div className="flex flex-wrap gap-2">
            {[
              { label: "Today", s: todayStr(), e: todayStr() },
              { label: "Yesterday", s: yesterdayStr(), e: yesterdayStr() },
              { label: "Last 7 Days", s: daysAgoStr(7), e: todayStr() },
              { label: "Last Month", s: daysAgoStr(30), e: todayStr() },
            ].map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => applyPreset(p.s, p.e)}
                className="rounded-md border border-[#676d78] bg-[#4f5561] px-3 py-1 text-xs text-white transition-colors hover:bg-[#5a616f]"
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Data sources */}
          <div>
            <label className="mb-2 block text-sm font-medium">Data Sources</label>
            <div className="space-y-1">
              {[
                { key: "tiktok" as const, label: "TikTok (Costs)" },
                { key: "roots" as const, label: "Roots (Revenue)" },
                { key: "adw" as const, label: "ADW (Revenue)" },
                { key: "klow" as const, label: "KLOW (Revenue)" },
                { key: "adwKeywords" as const, label: "ADW (Keywords)" },
              ].map((src) => (
                <label key={src.key} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={sources[src.key]}
                    onChange={() =>
                      setSources((prev) => ({
                        ...prev,
                        [src.key]: !prev[src.key],
                      }))
                    }
                    className="accent-[#8b9aff]"
                  />
                  <span className="text-sm">{src.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Account selection */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-medium">Accounts</label>
              <button
                type="button"
                onClick={toggleSelectAll}
                className="text-xs text-[#8b9aff] hover:underline"
              >
                {selectAll ? "Deselect All" : "Select All"}
              </button>
            </div>
            <div className="max-h-40 space-y-1 overflow-y-auto rounded border border-[#4a4a4a] bg-[#3a3a3a] p-2">
              {accounts.length === 0 ? (
                <p className="py-2 text-center text-xs text-white">No accounts</p>
              ) : (
                accounts.map((acc) => (
                  <label key={acc.advertiser_id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectAll || selectedAccounts.has(acc.advertiser_id)}
                      onChange={() => toggleAccount(acc.advertiser_id)}
                      className="accent-[#8b9aff]"
                    />
                    <span className="text-xs">
                      {acc.advertiser_name} ({acc.advertiser_id})
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-[#7a808b]/45 bg-[#636b78] px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#58606c]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
            className="rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#687bda] disabled:opacity-50"
            >
              {isPending ? "Starting..." : "🔄 Start Sync"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<string, string> = {
  running: "bg-blue-900/40 text-blue-300 border-blue-700",
  queued: "bg-amber-900/40 text-amber-300 border-amber-700",
  completed: "bg-green-900/40 text-green-300 border-green-700",
  timeout: "bg-amber-900/40 text-amber-300 border-amber-700",
  failure: "bg-red-900/40 text-red-300 border-red-700",
  failed: "bg-red-900/40 text-red-300 border-red-700",
}

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_STYLES[status] ?? "bg-[#555] text-white"
  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Main Tab
// ---------------------------------------------------------------------------

export default function SyncTab() {
  const [syncOpen, setSyncOpen] = useState(false)
  const activeJobs = useActiveSyncJobs(true)
  const cancelMut = useCancelSyncJob()
  const lastSync = useLastSyncInfo()

  const hasActive = activeJobs.data?.has_active_sync ?? false
  const jobs = activeJobs.data?.active_jobs ?? []

  const lastSyncText = useMemo(() => {
    const rows = lastSync.data?.data
    if (!rows?.length) return "No sync history"
    const row = rows[0]
    if (!row.started_at) return "Unknown"
    return `${new Date(row.started_at).toLocaleString()} — ${row.status}`
  }, [lastSync.data])

  async function handleCancel(logId: number) {
    try {
      await cancelMut.mutateAsync(logId)
      showToast("Job cancelled")
    } catch {
      showToast("Failed to cancel job", "error")
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="border-b-2 border-[#8b9aff] pb-2 text-2xl font-semibold text-white">
            Data Sync
          </h2>
          <p className="mt-1 text-sm text-white">
            Manually sync campaign data from TikTok, revenue data from Roots, and revenue data from ADW
            for selected accounts and date ranges.
          </p>
          <p className="mt-1 text-xs text-white">Last Sync: {lastSyncText}</p>
        </div>
        <button
          onClick={() => setSyncOpen(true)}
          className="rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-[#687bda]"
        >
          🔄 Sync Data
        </button>
      </div>

      {/* Active jobs */}
      {hasActive && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-white">Active Sync Jobs</h4>
          <div className="overflow-x-auto rounded-lg border border-[#4a4a4a]">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#444444] text-white">
                <tr>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Started</th>
                  <th className="px-4 py-2 font-medium">Description</th>
                  <th className="px-4 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#4a4a4a]">
                {jobs.map((job) => (
                  <tr key={job.log_id} className="transition-colors hover:bg-[#444444]">
                    <td className="px-4 py-2 text-white">{job.job_type}</td>
                    <td className="px-4 py-2">
                      <StatusBadge status={job.status} />
                    </td>
                    <td className="px-4 py-2 text-xs text-white">
                      {job.started_at ? new Date(job.started_at).toLocaleTimeString() : "—"}
                    </td>
                    <td className="max-w-[300px] truncate px-4 py-2 text-xs text-white">
                      {job.description}
                    </td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => handleCancel(job.log_id)}
                        disabled={cancelMut.isPending}
                        className="rounded-md border border-[#d26a78]/35 bg-[#c6596a] px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#b84f60]"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!hasActive && (
        <p className="rounded-lg border border-[#4a4a4a] bg-[#3a3a3a] px-4 py-8 text-center text-sm text-white">
          No active sync jobs.
        </p>
      )}

      <SyncModal open={syncOpen} onClose={() => setSyncOpen(false)} />
    </div>
  )
}
